import { formatSweepFrequencyLabel, getSweepFrequencyDisplayUnit, type SweepFrequencyDisplayUnit } from './frequency';

export const AXIS_TICK_EPSILON = 1e-6;
export const AXIS_LABEL_FONT = '11px monospace';
const AXIS_LABEL_MIN_SPACING_PX = 56;
const AXIS_LABEL_PADDING_PX = 24;
const AXIS_MINOR_TICK_MIN_SPACING_PX = 10;

export function getNiceAxisStepMHz(roughMHz: number): number {
    if (roughMHz <= 0 || !Number.isFinite(roughMHz)) {
        return 1;
    }

    const exponent = Math.floor(Math.log10(roughMHz));
    const magnitude = Math.pow(10, exponent);
    const normalized = roughMHz / magnitude;

    if (normalized <= 1) {
        return magnitude;
    }

    if (normalized <= 2) {
        return 2 * magnitude;
    }

    if (normalized <= 5) {
        return 5 * magnitude;
    }

    return 10 * magnitude;
}

export function isAxisStepAligned(frequencyMHz: number, stepMHz: number): boolean {
    if (stepMHz <= 0 || !Number.isFinite(stepMHz)) {
        return false;
    }

    const quotient = frequencyMHz / stepMHz;

    return Math.abs(quotient - Math.round(quotient)) < AXIS_TICK_EPSILON;
}

function getAxisSampleFrequenciesMHz(params: {
    stepMHz: number;
    totalMHz: number;
    visibleEndMHz: number;
    visibleStartMHz: number;
}): number[] {
    const { stepMHz, totalMHz, visibleEndMHz, visibleStartMHz } = params;
    const sampleMap = new Map<string, number>();

    const addSample = (value: number) => {
        const clampedValue = Math.max(0, Math.min(totalMHz, value));

        if (clampedValue < visibleStartMHz - AXIS_TICK_EPSILON || clampedValue > visibleEndMHz + AXIS_TICK_EPSILON) {
            return;
        }

        sampleMap.set(clampedValue.toFixed(9), clampedValue);
    };

    const startTick = Math.ceil(visibleStartMHz / stepMHz) * stepMHz;
    const endTick = Math.floor(visibleEndMHz / stepMHz) * stepMHz;
    const middleTick = Math.round((visibleStartMHz + visibleEndMHz) / 2 / stepMHz) * stepMHz;

    addSample(startTick);
    addSample(startTick + stepMHz);
    addSample(middleTick);
    addSample(endTick - stepMHz);
    addSample(endTick);

    if (visibleStartMHz <= 0 && visibleEndMHz >= 0) {
        addSample(0);
    }

    if (sampleMap.size === 0) {
        addSample(visibleStartMHz);
    }

    return Array.from(sampleMap.values());
}

function getAxisMaxLabelWidthPx(
    ctx: CanvasRenderingContext2D,
    params: {
        stepMHz: number;
        totalMHz: number;
        unit: SweepFrequencyDisplayUnit;
        visibleEndMHz: number;
        visibleStartMHz: number;
    },
): number {
    const { stepMHz, totalMHz, unit, visibleEndMHz, visibleStartMHz } = params;

    return getAxisSampleFrequenciesMHz({
        stepMHz,
        totalMHz,
        visibleEndMHz,
        visibleStartMHz,
    }).reduce((maxWidthPx, frequencyMHz) => {
        const label = formatSweepFrequencyLabel({
            frequencyMHz,
            stepMHz,
            unit,
        });

        return Math.max(maxWidthPx, ctx.measureText(label).width);
    }, 0);
}

export function getAxisLabelStepMHz(params: {
    ctx: CanvasRenderingContext2D;
    pxPerMHz: number;
    totalMHz: number;
    visibleEndMHz: number;
    visibleStartMHz: number;
}): number {
    const { ctx, pxPerMHz, totalMHz, visibleEndMHz, visibleStartMHz } = params;
    const roughMinimumStepMHz = 24 / Math.max(1e-6, pxPerMHz);
    const baseExponent = Math.floor(Math.log10(Math.max(roughMinimumStepMHz, AXIS_TICK_EPSILON)));
    const candidateStepsMHz: number[] = [];

    for (let exponent = baseExponent - 1; exponent <= baseExponent + 8; exponent += 1) {
        const magnitude = 10 ** exponent;

        for (const multiplier of [1, 2, 5]) {
            candidateStepsMHz.push(multiplier * magnitude);
        }
    }

    candidateStepsMHz.sort((left, right) => left - right);

    for (const candidateStepMHz of candidateStepsMHz) {
        const stepWidthPx = candidateStepMHz * pxPerMHz;

        if (stepWidthPx < 24) {
            continue;
        }

        const axisUnit = getSweepFrequencyDisplayUnit({
            stepMHz: candidateStepMHz,
            visibleEndMHz,
            visibleStartMHz,
        });
        const maxLabelWidthPx = getAxisMaxLabelWidthPx(ctx, {
            stepMHz: candidateStepMHz,
            totalMHz,
            unit: axisUnit,
            visibleEndMHz,
            visibleStartMHz,
        });
        const requiredStepWidthPx = Math.max(AXIS_LABEL_MIN_SPACING_PX, maxLabelWidthPx + AXIS_LABEL_PADDING_PX);

        if (stepWidthPx >= requiredStepWidthPx) {
            return candidateStepMHz;
        }
    }

    return getNiceAxisStepMHz((AXIS_LABEL_MIN_SPACING_PX + AXIS_LABEL_PADDING_PX) / Math.max(1e-6, pxPerMHz));
}

export function getAxisMinorStepMHz(labelStepMHz: number, pxPerMHz: number): number | null {
    const candidateStepMHz = labelStepMHz / 5;

    if (candidateStepMHz * pxPerMHz < AXIS_MINOR_TICK_MIN_SPACING_PX) {
        return null;
    }

    return candidateStepMHz;
}
